import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import { S } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/utils/attendanceConstants';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { META } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/utils/attendanceConstants';

/* -------- utils -------- */
const clean = (x) => {
  const s = (x ?? '').toString().trim().replace(/\s+/g, ' ');
  return s.length ? s : '';
};
const upper = (x) => clean(x).toUpperCase();
const pad2 = (n) => String(n).padStart(2, '0');
const minToHm = (min) => {
  const v = Number.isFinite(min) ? min : 0;
  return `${pad2(Math.floor(v / 60))}:${pad2(v % 60)}`;
};

// eğitmene göre deterministik renk (kategori)
// eğitmene göre deterministik renk (kategori)
const PALETTE = [
  { h: 210, s: 85, l: 45 }, // blue
  { h: 45, s: 90, l: 45 }, // amber
  { h: 285, s: 70, l: 48 }, // purple
  { h: 25, s: 85, l: 48 }, // orange
  { h: 195, s: 80, l: 40 }, // teal

  { h: 240, s: 70, l: 50 }, // indigo
  { h: 310, s: 75, l: 52 }, // magenta (yeni)
  { h: 170, s: 70, l: 40 }, // emerald/teal2 (yeni)
];

const hashStr = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return h >>> 0;
};
const colorByTrainer = (name) => {
  const key = clean(name || '—');
  const p = PALETTE[hashStr(key) % PALETTE.length];
  const fg = `hsl(${p.h} ${p.s}% ${p.l}%)`;
  return {
    key,
    fg,
    soft: `hsla(${p.h}, ${p.s}%, ${p.l}%, 0.10)`,
    bd: `hsla(${p.h}, ${p.s}%, ${p.l}%, 0.22)`,
  };
};
const BILLING_COLORS = {
  PACKAGE: { fg: '#16a34a' }, // green
  PAID: { fg: '#f59e0b' }, // amber
  FREE: { fg: '#3b82f6' }, // blue
  UNKNOWN: { fg: '#64748b' },
};

const resolveBillingMode = (s) => {
  const bm = upper(s?.billingMode);
  if (bm) return bm;

  const ps = Array.isArray(s?.participants) ? s.participants : [];
  const set = new Set(ps.map((p) => upper(p?.billingMode)).filter(Boolean));
  if (set.size === 1) return [...set][0];

  return 'UNKNOWN';
};

const billingTone = (billingMode) => {
  const key = BILLING_COLORS[billingMode] ? billingMode : 'UNKNOWN';
  const fg = BILLING_COLORS[key].fg;
  return {
    key,
    fg,
    softBg: (isDark) => (isDark ? alpha(fg, 0.2) : alpha(fg, 0.14)),
    border: (isDark) => (isDark ? alpha(fg, 0.42) : alpha(fg, 0.3)),
    // chip üstünde okunaklı yazı rengi:
    textOnSoft: (isDark) => (isDark ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.92)'),
  };
};

const isTelafi = (s) => upper(s?.sessionType || s?.type) === 'TELAFI';

const sessionTypeLabel = (t) => {
  const x = upper(t);
  if (!x || x === 'NORMAL') return '';
  if (x === 'TELAFI') return 'telafi';
  return x.toLowerCase();
};

/* -------- density system (wide / medium / narrow) -------- */
const densityByWidth = (w) => {
  if (!Number.isFinite(w)) return 'wide';
  if (w < 260) return 'narrow';
  if (w < 360) return 'medium';
  return 'wide';
};

const TOKENS = {
  wide: {
    pad: 8,
    pl: 11,
    minH: 70,
    timeFs: 13.5,
    capFs: 11.2,
    capH: 20,
    chipH: 20,
    chipFs: 10.4,
    chipMax: 160,
    labelFs: 12,
    gap: 5,
    chipsDir: { xs: 'row', sm: 'row' },
    membersLines: 1,
    showLabels: false,
    actionsPadR: 50,
    actBtn: 25,
    actIcon: 18,
    actGap: 6,
    selectFs: 10,
    selectPx: 8,
    selectPy: 3,
    actionY: 4,
  },

  medium: {
    pad: 7,
    pl: 10,
    minH: 66,
    timeFs: 13,
    capFs: 11,
    capH: 20,
    chipH: 19,
    chipFs: 10.2,
    chipMax: 140,
    labelFs: 11.6,
    gap: 7,
    chipsDir: { xs: 'row', sm: 'row' }, // medium’de chips yan yana daha stabil
    membersLines: 2,
    showLabels: false,
    actionsPadR: 45,
    actBtn: 23,
    actIcon: 17,
    actGap: 5,
    selectFs: 10,
    selectPx: 7,
    selectPy: 3,
    actionY: 5,
  },
  narrow: {
    pad: 6,
    pl: 9,
    minH: 60,
    timeFs: 10,
    capFs: 10.6,
    capH: 18,
    chipH: 18,
    chipFs: 9.6,
    chipMax: 120,
    labelFs: 10,
    gap: 3,
    chipsDir: { xs: 'row', sm: 'row' },
    membersLines: 5,
    showLabels: false,
    actionsPadR: 10,
    actBtn: 21,
    actIcon: 16,
    actGap: 4,
    selectFs: 9,
    selectPx: 6,
    selectPy: 2,
    actionY: 8,
  },
};

const chipSx = (theme, density, variant = 'soft') => {
  const isDark = theme.palette.mode === 'dark';
  const t = TOKENS[density];
  return {
    height: t.chipH,
    borderRadius: '999px',
    fontWeight: 900,
    background:
      variant === 'soft' ? alpha(theme.palette.text.primary, isDark ? 0.1 : 0.06) : 'transparent',
    border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.16 : 0.12)}`,
  };
};

/* -------- member formatting -------- */
const splitFullName = (full) => {
  const x = clean(full);
  if (!x) return { first: '', last: '' };
  const parts = x.split(' ').filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const formatMembersForDensity = (members, density) => {
  const list = members.map(splitFullName);

  // wide: “Kerem Turak, Ali Ekber +2”
  if (density === 'wide') {
    const shown = members.slice(0, 2);
    const more = members.length - shown.length;
    return more > 0 ? `${shown.join(', ')} +${more}` : shown.join(', ');
  }

  // medium: “Kerem Turak, Ali Ekber +2 kişi”
  if (density === 'medium') {
    const shown = members.slice(0, 2);
    const more = members.length - shown.length;
    return more > 0 ? `${shown.join(', ')} +${more} kişi` : shown.join(', ');
  }

  // narrow: sadece isimler: “Kerem, Ali +2”
  const shown = list
    .slice(0, 2)
    .map((n) => n.first || '')
    .filter(Boolean);

  const more = members.length - shown.length;
  const base = shown.join(', ');
  return more > 0 ? `${base} +${more}` : base;
};

export function SoftSessionCard({
  s,
  onClick,

  onDetail,
  onAttendance,
  onEdit,
  onCancel,
  onDelete,
  editMode = false, // ✅ EKLE
  onMove,
  forceClickable = false,
  copyMode = false,
  selected = false, // opsiyonel (seçilen source’u göstermek için)
}) {
  const normAtt = (x) => {
    const v = upper(x);
    if (!v) return '';

    if (v === 'ATTENDED' || v === 'PRESENT' || v === 'DONE') return S.PRESENT;
    if (v === 'ABSENT' || v === 'NO_SHOW' || v === 'NOSHOW') return S.ABSENT;
    if (v === 'CANCELLED' || v === 'CANCELED') return S.CANCELLED;
    if (v === 'BURNED' || v === 'BURN' || v === 'BURNED_OUT') return S.BURN;

    if (v === S.PENDING || v === S.PRESENT || v === S.ABSENT || v === S.CANCELLED || v === S.BURN)
      return v;

    return '';
  };

  // üstlere ekle
  const asCount = (x) => {
    if (typeof x === 'boolean') return x ? 1 : 0;
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  };

  const resolveAttendanceState = (s) => {
    // ✅ 0) direct fields (status dahil)
    const direct = normAtt(
      s?.attendanceStatus || s?.currentAttendanceStatus || s?.status // 👈 SENDEDEN geliyor
    );
    if (direct) return direct;

    // ✅ 1) weekly summary alanlarından türet (boolean/null safe)
    const attended = asCount(s?.attended ?? s?.attendedCount);
    const cancelled = asCount(s?.cancelled ?? s?.cancelledCount);
    const burned = asCount(s?.burned ?? s?.burnedCount);
    const noShow = asCount(s?.noShow ?? s?.noShowCount ?? s?.absent);

    // öncelik: BURN > CANCELLED > NO_SHOW > ATTENDED
    if (burned > 0) return S.BURN;
    if (cancelled > 0) return S.CANCELLED;
    if (noShow > 0) return S.ABSENT;
    if (attended > 0) return S.PRESENT;

    // ✅ 2) participants detail varsa
    const ps = Array.isArray(s?.participants) ? s.participants : [];
    const states = ps
      .map((p) => normAtt(p?.attendanceStatus || p?.current?.status || p?.status))
      .filter(Boolean);

    if (!states.length) return S.PENDING;
    if (states.includes(S.BURN)) return S.BURN;
    if (states.includes(S.CANCELLED)) return S.CANCELLED;
    if (states.includes(S.ABSENT)) return S.ABSENT;
    if (states.every((x) => x === S.PRESENT)) return S.PRESENT;

    return S.PENDING;
  };
  const normRsvp = (x) => {
    const v = upper(x);
    if (!v) return '';
    if (v === 'YES' || v === 'NO' || v === 'UNKNOWN') return v;
    return '';
  };

  const resolveRsvpState = (s) => {
    const ps = Array.isArray(s?.participants) ? s.participants : [];
    const rs = ps.map((p) => normRsvp(p?.rsvpStatus)).filter(Boolean);

    if (!rs.length) return ''; // hiç bilgi yok
    const set = new Set(rs);

    // tümü YES -> YES
    if (set.size === 1 && set.has('YES')) return 'YES';

    // tümü NO -> NO
    if (set.size === 1 && set.has('NO')) return 'NO';

    // karışık (bir kısmı YES bir kısmı NO / UNKNOWN) -> MIXED
    if (set.has('YES') && set.has('NO')) return 'MIXED';

    // sadece UNKNOWN varsa
    if (set.size === 1 && set.has('UNKNOWN')) return 'UNKNOWN';

    // UNKNOWN + YES/NO gibi durumları da MIXED say
    return 'MIXED';
  };
  const summarizeRsvp = (s) => {
    const ps = Array.isArray(s?.participants) ? s.participants : [];
    const counts = { YES: 0, NO: 0, UNKNOWN: 0 };

    ps.forEach((p) => {
      const key = normRsvp(p?.rsvpStatus);
      if (key) counts[key] += 1;
    });

    return {
      yes: counts.YES,
      no: counts.NO,
      unknown: counts.UNKNOWN,
      total: counts.YES + counts.NO + counts.UNKNOWN,
    };
  };
  const isFutureSessionTR = (s) => {
    const day = s?.dayISO;
    const startMin = Number.isFinite(s?.startMin) ? s.startMin : 0;
    if (!day) return false;

    // dayISO "YYYY-MM-DD" string geliyor
    const [y, m, d] = String(day).split('-').map(Number);
    if (!y || !m || !d) return false;

    const now = new Date();
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // local (TR)
    const sessDay = new Date(y, m - 1, d);

    // yarın ve sonrası direkt future
    if (sessDay.getTime() > nowDay.getTime()) return true;

    // bugünse: seans saati > şimdi mi?
    if (sessDay.getTime() === nowDay.getTime()) {
      const nowMin = now.getHours() * 60 + now.getMinutes();
      return startMin > nowMin;
    }

    return false; // geçmiş
  };

  const billingMode = resolveBillingMode(s);

  function attendanceTone(state, isDark) {
    const tones = {
      [S.PRESENT]: { fg: '#16a34a', bgA: isDark ? 0.18 : 0.12, bdA: isDark ? 0.45 : 0.3 },
      [S.ABSENT]: { fg: '#64748b', bgA: isDark ? 0.18 : 0.1, bdA: isDark ? 0.4 : 0.26 },
      [S.CANCELLED]: { fg: '#f59e0b', bgA: isDark ? 0.16 : 0.11, bdA: isDark ? 0.42 : 0.28 },
      [S.BURN]: { fg: '#ef4444', bgA: isDark ? 0.18 : 0.12, bdA: isDark ? 0.46 : 0.3 },
      [S.PENDING]: { fg: null, bgA: 0, bdA: 0 },
    };
    return tones[state] || tones[S.PENDING];
  }
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const [hovered, setHovered] = useState(false);

  // ✅ card width observer -> density
  const ref = useRef(null);
  const [density, setDensity] = useState('wide');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const apply = () => {
      const w = el.getBoundingClientRect().width;
      setDensity(densityByWidth(w));
    };

    apply();
    const ro = new ResizeObserver(() => apply());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const d = useMemo(() => {
    const billingMode = resolveBillingMode(s);
    const attendanceState = resolveAttendanceState(s);
    const startMin = Number.isFinite(s?.startMin) ? s.startMin : 0;
    const rsvpState = resolveRsvpState(s);
    const rsvpSummary = summarizeRsvp(s);
    const isFuture = isFutureSessionTR(s);

    const activity = clean(s?.activityName || s?.title) || 'Seans';
    const type = sessionTypeLabel(s?.sessionType || s?.type);

    const trainer = clean(s?.trainerName) || '—';
    const toTitleTR = (str) => {
      const s = clean(str);
      if (!s) return '';
      // tr-TR locale ile büyük/küçük harf dönüşümü
      return s
        .toLocaleLowerCase('tr-TR')
        .split(' ')
        .filter(Boolean)
        .map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1))
        .join(' ');
    };

    const members = Array.isArray(s?.participants)
      ? s.participants
          .map((p) => toTitleTR(p?.fullName || p?.memberNameSnapshot || p?.memberName))
          .filter(Boolean)
      : Array.isArray(s?.members)
      ? s.members.map((x) => toTitleTR(x)).filter(Boolean)
      : [];

    const cap = Number.isFinite(s?.capacity) ? s.capacity : Number.isFinite(s?.cap) ? s.cap : 1;

    const booked = Number.isFinite(s?.participantCount)
      ? s.participantCount
      : Number.isFinite(s?.booked)
      ? s.booked
      : members.length;

    return {
      startMin,
      activity,
      type,
      trainer,
      members,
      cap,
      booked,
      billingMode,
      attendanceState,

      // ✅ EKLE
      rsvpState,
      rsvpSummary,
      isFuture,
    };
  }, [s]);

  const tc = useMemo(() => colorByTrainer(d.trainer), [d.trainer]);
  const t = TOKENS[density];

  // menu
  // ✅ edit modunda: inline ikonlar (edit + delete)
  // ✅ normal modda: aksiyon yok (3 nokta da yok)
  const showInlineActions = Boolean(editMode && (onMove || onEdit || onDelete || copyMode));

  const handleInlineEdit = useCallback(
    (e) => {
      e?.stopPropagation?.();
      onEdit?.(s);
    },
    [onEdit, s]
  );

  const handleInlineDelete = useCallback(
    (e) => {
      e?.stopPropagation?.();
      onDelete?.(s);
    },
    [onDelete, s]
  );
  const handleInlineMove = useCallback(
    (e) => {
      e?.stopPropagation?.();
      onMove?.(s);
    },
    [onMove, s]
  );

  function makeTextMeasurer(fontCss) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = fontCss;
    return (text) => ctx.measureText(text || '').width;
  }
  const initials2 = (first, last) => {
    const a = (first || '').trim().charAt(0).toUpperCase();
    const b = (last || '').trim().charAt(0).toUpperCase();
    if (!a && !b) return '';
    return `${a}${b || ''}`; // "KT" gibi
  };
  const bt = useMemo(() => billingTone(d.billingMode), [d.billingMode]);
  const buildMembersCandidates = (members) => {
    const list = (members || []).map(splitFullName).filter((x) => x.first);

    const full2 = list
      .slice(0, 2)
      .map((n) => clean(`${n.first} ${n.last}`))
      .filter(Boolean);
    const first2 = list
      .slice(0, 2)
      .map((n) => clean(n.first))
      .filter(Boolean);
    const init2 = list
      .slice(0, 2)
      .map((n) => initials2(n.first, n.last))
      .filter(Boolean);

    const more = Math.max(0, list.length - 2);

    // not: +more’ları istersen her kademeye ekleyebilirsin
    const addMoreInline = (base) => (more > 0 ? `${base} +${more}` : base);

    return [
      // 1) Üyeler: Kerem Turak, Ali Ekber
      { level: 1, label: true, multiline: false, text: addMoreInline(full2.join(', ')) },

      // 2) Üyeler: Kerem, Ali
      { level: 2, label: true, multiline: false, text: addMoreInline(first2.join(', ')) },

      // 3) Kerem, Ali
      { level: 3, label: false, multiline: false, text: addMoreInline(first2.join(', ')) },

      // 4) Kerem\nAli

      // 5) KT, AE
    ];
  };
  const pickBestMembersText = ({ members, availablePx, fontCss }) => {
    const candidates = buildMembersCandidates(members);
    const measure = makeTextMeasurer(fontCss);

    // label varsa “Üyeler: ” ekinin genişliğini de kat
    const labelPrefix = 'Üyeler: ';

    for (const c of candidates) {
      const s = c.label ? `${labelPrefix}${c.text}` : c.text;

      // multiline ise: en geniş satır sığmalı
      if (c.multiline) {
        const lines = (s || '').split('\n');
        const maxLine = Math.max(...lines.map((x) => measure(x)));
        if (maxLine <= availablePx) return { ...c, rendered: s };
      } else {
        if (measure(s) <= availablePx) return { ...c, rendered: s };
      }
    }

    // hiçbir şey sığmazsa en agresif
    const last = candidates[candidates.length - 1];
    return { ...last, rendered: last.label ? `Üyeler: ${last.text}` : last.text };
  };
  const membersRef = useRef(null);
  const [membersW, setMembersW] = useState(0);

  useEffect(() => {
    const el = membersRef.current;
    if (!el) return;

    const apply = () => setMembersW(el.getBoundingClientRect().width || 0);
    apply();

    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const membersText = useMemo(
    () => formatMembersForDensity(d.members, density),
    [d.members, density]
  );
  const handleCardClick = useCallback(
    (e) => {
      if (editMode && !forceClickable) return; // ✅ sadece zorlanmadıysa pasif
      onDetail?.(s); // ✅ normalde detay aç
      onClick?.(e); // (istersen kalsın)
    },
    [editMode, forceClickable, onDetail, onClick, s]
  );
  const clickable = (forceClickable || !editMode) && (onDetail || onClick);
  const ring = useMemo(() => {
    if (selected) {
      return {
        outline: '2px solid #3b82f6',
        outlineOffset: '-2px',
        boxShadow: 'inset 0 0 0 999px rgba(59,130,246,0.08)',
      };
    }
    if (copyMode) {
      return {
        outline: '2px solid rgba(245,158,11,0.75)',
        outlineOffset: '-2px',
        boxShadow: `0 10px 26px rgba(245,158,11,${isDark ? 0.16 : 0.22}),
                  inset 0 0 0 999px rgba(245,158,11,${isDark ? 0.05 : 0.06})`,
      };
    }
    return {};
  }, [copyMode, selected, isDark]);

  const telafi = isTelafi(s);
  const red = '#ef4444'; // güçlü kırmızı
  const att = d.attendanceState || S.PENDING;
  const attMeta = META?.[att];
  const at = attendanceTone(att, isDark);

  // attendance bazlı soft bg/border (TELAFI override etmezsek karışır)
  // Öncelik: TELAFI -> senin kırmızı telafi tasarımın kalsın
  const attBg = at.fg
    ? isDark
      ? `linear-gradient(180deg, ${alpha(at.fg, at.bgA)} 0%, rgba(255,255,255,0.03) 100%)`
      : `linear-gradient(180deg, ${alpha(at.fg, at.bgA)} 0%, rgba(255,255,255,0.95) 100%)`
    : null;

  const attBorder = at.fg ? alpha(at.fg, at.bdA) : null;

  // TELAFI kırmızı yoğunlukları (dark/light ayrı)
  const telafiBg = telafi
    ? isDark
      ? `linear-gradient(180deg,
          ${alpha(red, 0.18)} 0%,
          ${alpha(red, 0.1)} 55%,
          rgba(255,255,255,0.03) 100%)`
      : `linear-gradient(180deg,
          ${alpha(red, 0.18)} 0%,
          ${alpha(red, 0.1)} 55%,
          rgba(255,255,255,0.92) 100%)`
    : null;
  const RSVP_META = {
    YES: { icon: 'event_available', fg: '#16a34a', label: 'Katılacak' },
    NO: { icon: 'event_busy', fg: '#ef4444', label: 'Katılmayacak' },
    MIXED: { icon: 'groups', fg: '#f59e0b', label: 'Karışık' },
    UNKNOWN: { icon: 'help_outline', fg: '#64748b', label: 'Belirsiz' },
  };
  const telafiBorder = telafi ? alpha(red, isDark ? 0.55 : 0.45) : null;

  const telafiGlow = telafi
    ? `0 16px 34px rgba(239,68,68,${isDark ? 0.3 : 0.22}),
     0 0 0 1px ${alpha(red, isDark ? 0.22 : 0.18)}`
    : null;
  const showAttendanceBadge = att && att !== S.PENDING; // yoklama varsa
  const showRsvpBadge =
    !showAttendanceBadge && d.isFuture && d.rsvpState && d.rsvpState !== 'UNKNOWN';
  const showRsvpSummary = d.isFuture && d.rsvpSummary?.total > 0;

  const rsvpMeta = RSVP_META[d.rsvpState] || null;

  return (
    <MDBox
      className="SoftSessionCardRoot"
      ref={ref}
      onClick={handleCardClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (editMode) return;
        if (e.key === 'Enter' || e.key === ' ') handleCardClick(e);
      }}
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        transition: '140ms ease',
        cursor: copyMode || clickable ? 'pointer' : 'default',

        ...(selected
          ? {
              outline: '2px solid #3b82f6',
              outlineOffset: '-2px',
              boxShadow: 'inset 0 0 0 999px rgba(59,130,246,0.08)',
            }
          : copyMode
          ? {
              outline: '2px solid rgba(245,158,11,0.75)',
              outlineOffset: '-2px',
              boxShadow: `0 10px 26px rgba(245,158,11,${isDark ? 0.16 : 0.22}),
               inset 0 0 0 999px rgba(245,158,11,${isDark ? 0.05 : 0.06})`,
            }
          : {}),

        '&:hover': {
          borderColor: attBorder
            ? alpha(at.fg, isDark ? 0.7 : 0.55)
            : alpha(tc.fg, isDark ? 0.28 : 0.22),

          background: attBg ? attBg : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,1)',

          ...(copyMode
            ? {
                outline: '2px solid rgba(245,158,11,0.98)',
                outlineOffset: '-2px',
                boxShadow: `0 14px 32px rgba(245,158,11,${isDark ? 0.22 : 0.32}),
                 inset 0 0 0 999px rgba(245,158,11,${isDark ? 0.07 : 0.08})`,
                transform: 'translateY(-1px)',
              }
            : {
                boxShadow: `0 10px 26px rgba(0,0,0,${isDark ? 0.2 : 0.1})`,
                transform: 'translateY(-1px)',
              }),
        },

        // ✅ final appearance (TELAFI yok)
        border: attBorder
          ? `1px solid ${attBorder}`
          : `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.12 : 0.1)}`,

        background: attBg ? attBg : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.92)',
      }}
    >
      {/* ✅ COPY MODE overlay badge */}
      {copyMode ? (
        <MDBox
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 50,
            pointerEvents: 'none',
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(-2px)',
            transition: '140ms ease',
          }}
        ></MDBox>
      ) : null}
      {/* ✅ Attendance 상태 badge (sağ üst) */}

      {/* sol ince renk şeridi */}
      <MDBox
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: tc.fg,
          opacity: 0.9,
        }}
      />
      <MDBox
        sx={{
          display: 'grid',
          position: 'relative', // ✅ overlay için şart
          gridTemplateColumns: '1fr auto',
          gridTemplateRows: 'auto auto auto', // ✅ SON SATIR esnesin
          minHeight: t.minH, // ✅ kartın min yüksekliği garanti
          alignItems: 'start',
          rowGap: `${Math.max(1, t.gap - 3)}px`,
          columnGap: '6px',
          p: `${t.pad}px`,
          pl: `${t.pl}px`,
        }}
      >
        {/* ROW 1 / COL 1: saat */}
        {/* ROW 1 / COL 1: saat + attendance icon (bitişik) */}
        <MDBox
          sx={{
            minWidth: 0,
            gridColumn: '1 / 2',
            gridRow: '1 / 2',
            display: 'inline-flex',
            alignItems: 'center',
            mt: '-1px',
            position: 'relative', // ✅ overlay için
          }}
        >
          <MDTypography
            variant="button"
            sx={{
              fontSize: t.timeFs,
              fontWeight: 1000,
              letterSpacing: 0.2,
              color: theme.palette.text.primary,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum"',
              minWidth: 'unset', // ✅ 54’ü kaldır
              display: 'inline-block',
              pr: 0, // ✅ sağ padding yok
            }}
          >
            {minToHm(d.startMin)}
          </MDTypography>

          {showAttendanceBadge ? (
            <Tooltip
              title={`Yoklama: ${attMeta?.label || att}`}
              arrow
              enterDelay={250}
              disableInteractive
            >
              <MDBox
                onClick={(e) => e.stopPropagation()}
                sx={{
                  ml: '-10px',
                  transform: 'translateX(11px)',
                  width: 14,
                  height: 14,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: alpha(at.fg || theme.palette.text.primary, isDark ? 0.16 : 0.12),
                  border: `1px solid ${alpha(
                    at.fg || theme.palette.text.primary,
                    isDark ? 0.28 : 0.22
                  )}`,
                }}
              >
                <Icon
                  sx={{ fontSize: 16, lineHeight: 1, color: at.fg || theme.palette.text.primary }}
                >
                  {/* ✅ attendance ikonlarını burada netleştirebilirsin */}
                  {att === S.PRESENT
                    ? 'verified'
                    : att === S.ABSENT
                    ? 'person_off'
                    : att === S.CANCELLED
                    ? 'event_busy'
                    : att === S.BURN
                    ? 'local_fire_department'
                    : 'info'}
                </Icon>
              </MDBox>
            </Tooltip>
          ) : showRsvpBadge && rsvpMeta ? (
            <Tooltip title={`RSVP: ${rsvpMeta.label}`} arrow enterDelay={250} disableInteractive>
              <MDBox
                onClick={(e) => e.stopPropagation()}
                sx={{
                  ml: '-10px',
                  transform: 'translateX(11px)',
                  width: 14,
                  height: 14,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: alpha(rsvpMeta.fg, isDark ? 0.16 : 0.12),
                  border: `1px solid ${alpha(rsvpMeta.fg, isDark ? 0.3 : 0.24)}`,
                }}
              >
                <Icon sx={{ fontSize: 16, lineHeight: 1, color: rsvpMeta.fg }}>
                  {rsvpMeta.icon}
                </Icon>
              </MDBox>
            </Tooltip>
          ) : null}
          {showRsvpSummary ? (
            <Tooltip
              title={`Katılacak: ${d.rsvpSummary.yes} • Katılmayacak: ${d.rsvpSummary.no} • Belirsiz: ${d.rsvpSummary.unknown}`}
              arrow
              enterDelay={200}
            >
              <MDBox
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.45,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '999px',
                  background: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.05),
                  border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`,
                }}
              >
                <Icon sx={{ fontSize: 13, color: '#16a34a' }}>check_circle</Icon>
                <MDTypography variant="caption" sx={{ fontWeight: 900, color: 'success.main' }}>
                  {d.rsvpSummary.yes}
                </MDTypography>
                <Icon sx={{ fontSize: 13, color: '#ef4444' }}>cancel</Icon>
                <MDTypography variant="caption" sx={{ fontWeight: 900, color: 'error.main' }}>
                  {d.rsvpSummary.no}
                </MDTypography>
                {d.rsvpSummary.unknown > 0 ? (
                  <>
                    <Icon sx={{ fontSize: 13, color: theme.palette.text.secondary }}>help</Icon>
                    <MDTypography
                      variant="caption"
                      sx={{ fontWeight: 900, color: 'text.secondary' }}
                    >
                      {d.rsvpSummary.unknown}
                    </MDTypography>
                  </>
                ) : null}
              </MDBox>
            </Tooltip>
          ) : null}
        </MDBox>

        {/* ROW 1 / COL 2: chips */}
        <MDBox
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Chip
            size="small"
            label={d.activity}
            sx={{
              ...chipSx(theme, density, 'soft'),
              maxWidth: t.chipMax,

              // 🔥 SADECE BU CHIP’İN ARKA PLANI
              background: bt.softBg(isDark),
              border: `1px solid ${bt.border(isDark)}`,

              '& .MuiChip-label': {
                px: 0.7,
                fontSize: t.chipFs,
                fontWeight: 900,
                color: bt.textOnSoft(isDark),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
          {d.type ? (
            <Chip
              size="small"
              label={d.type}
              sx={(theme) => {
                const isTelafi = d.type.toLowerCase() === 'telafi';

                return {
                  ...chipSx(theme, density, 'soft'), // 🔥 Yoga ile birebir aynı

                  background: '#ef4444',
                  border: '1px solid #dc2626',

                  '& .MuiChip-label': {
                    px: 0.7, // 🔥 Yoga ile birebir
                    fontSize: t.chipFs, // 🔥 Yoga ile birebir
                    fontWeight: 900,
                    color: '#ffffff',
                  },
                };
              }}
            />
          ) : null}
        </MDBox>

        {/* ROW 2 / COL 1-2: trainer (tek satır, full width) */}
        <MDBox
          sx={{
            gridColumn: '1 / 2', // ✅ sadece sol kolon (time'ın altı)
            gridRow: '2 / 3',
            minWidth: 0,
            mt: '-4px', // ✅ biraz daha yapıştır
          }}
        >
          {' '}
          <MDTypography
            variant="caption"
            sx={{
              fontSize: t.labelFs,
              fontWeight: 800,
              color: theme.palette.text.secondary,
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t.showLabels ? 'Eğitmen: ' : ''}
            <span style={{ color: tc.fg, fontWeight: 950 }}>{d.trainer}</span>
          </MDTypography>
        </MDBox>

        {/* ROW 3 / COL 1-2: MEMBERS (✅ artık gerçekten full width) */}
        <MDBox
          ref={membersRef}
          sx={{
            gridColumn: '1 / -1',
            gridRow: '3 / 4',
            minWidth: 0,
            mt: '-4px',
            pr: 0,
          }}
        >
          <Tooltip title={d.members.join(', ')} arrow disableHoverListener={!d.members.length}>
            <MDTypography
              variant="caption"
              sx={{
                fontSize: t.labelFs,
                fontWeight: 700,
                color: theme.palette.text.secondary,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    color: theme.palette.text.primary,
                    fontWeight: 900,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: '1 1 auto',
                  }}
                >
                  {membersText || '—'}
                </span>

                {!editMode ? (
                  <MDBox
                    sx={{
                      flex: '0 0 auto',
                      fontSize: 10,
                      fontWeight: 900,
                      px: 0.7,
                      py: 0.15,
                      borderRadius: '999px',
                      color: theme.palette.text.secondary,
                      background: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.06),
                      border: `1px solid ${alpha(
                        theme.palette.text.primary,
                        isDark ? 0.16 : 0.12
                      )}`,
                    }}
                  >
                    {d.booked}/{d.cap}
                  </MDBox>
                ) : null}
              </MDBox>
            </MDTypography>
          </Tooltip>
        </MDBox>

        {/* ROW 3 / COL 2: ACTIONS (✅ doğru satır/kolon) */}
        {/* ROW 3 / COL 2: ACTIONS */}
        <MDBox
          sx={{
            gridColumn: '2 / 3',
            gridRow: '2 / 3',
            alignSelf: 'center',
            mt: `${t.actionY}px`,
            display: 'flex',
            alignItems: 'center',
            gap: `${t.actGap}px`,
          }}
        >
          {showInlineActions ? (
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: `${t.actGap}px` }}>
              {/* ✅ COPY MODE: solda SEÇ pill + yanında (varsa) Taşı. Diğer ikonlar görünmesin */}
              {copyMode ? (
                <>
                  <MDBox
                    sx={{
                      flex: '0 0 auto',
                      fontSize: t.selectFs,
                      fontWeight: 950,
                      px: `${t.selectPx / 10}rem`, // istersen direkt sayı da verebilirsin
                      py: `${t.selectPy / 10}rem`,
                      borderRadius: '999px',
                      color: 'rgba(15,23,42,0.95)',
                      background: 'rgba(245,158,11,0.92)',
                      boxShadow: '0 10px 22px rgba(245,158,11,0.28)',
                      border: `1px solid ${alpha('#f59e0b', 0.45)}`,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    SEÇ
                  </MDBox>

                  {onMove ? (
                    <Tooltip title="Taşı" arrow>
                      <IconButton
                        size="small"
                        onClick={handleInlineMove}
                        sx={{
                          width: t.actBtn,
                          height: t.actBtn,
                          borderRadius: '999px',
                          background: alpha('#16a34a', 0.12),
                          border: `1px solid ${alpha('#16a34a', 0.3)}`,
                          transition: '140ms ease',
                          '& .MuiIcon-root': {
                            fontSize: t.actIcon,
                            color: '#16a34a',
                          },
                          '&:hover': {
                            background: alpha('#16a34a', 0.2),
                            boxShadow: '0 6px 16px rgba(22,163,74,0.35)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': { transform: 'scale(0.96)' },
                        }}
                      >
                        <Icon>open_with</Icon>
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </>
              ) : (
                <>
                  {/* ↔ TAŞI */}
                  {onMove ? (
                    <Tooltip title="Taşı" arrow>
                      <IconButton
                        size="small"
                        onClick={handleInlineMove}
                        sx={{
                          width: t.actBtn,
                          height: t.actBtn,
                          borderRadius: '999px',
                          background: alpha('#16a34a', 0.12),
                          border: `1px solid ${alpha('#16a34a', 0.3)}`,
                          transition: '140ms ease',
                          '& .MuiIcon-root': {
                            fontSize: t.actIcon,
                            color: '#16a34a',
                          },
                          '&:hover': {
                            background: alpha('#16a34a', 0.2),
                            boxShadow: '0 6px 16px rgba(22,163,74,0.35)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': { transform: 'scale(0.96)' },
                        }}
                      >
                        <Icon>open_with</Icon>
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  {/* ✏ DÜZENLE */}
                  {onEdit ? (
                    <Tooltip title="Düzenle" arrow>
                      <IconButton
                        size="small"
                        onClick={handleInlineEdit}
                        sx={{
                          width: t.actBtn,
                          height: t.actBtn,
                          borderRadius: '999px',
                          background: alpha('#f59e0b', 0.14),
                          border: `1px solid ${alpha('#f59e0b', 0.35)}`,
                          transition: '140ms ease',
                          '& .MuiIcon-root': { fontSize: t.actIcon, color: '#f59e0b' },
                          '&:hover': {
                            background: alpha('#f59e0b', 0.22),
                            boxShadow: '0 6px 16px rgba(245,158,11,0.45)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': { transform: 'scale(0.96)' },
                        }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  {/* 🗑 SİL */}
                  {onDelete ? (
                    <Tooltip title="Sil" arrow>
                      <IconButton
                        size="small"
                        onClick={handleInlineDelete}
                        sx={{
                          width: t.actBtn,
                          height: t.actBtn,
                          borderRadius: '999px',
                          background: alpha('#ef4444', 0.1),
                          border: `1px solid ${alpha('#ef4444', 0.28)}`,
                          transition: '140ms ease',
                          '& .MuiIcon-root': { fontSize: t.actIcon, color: '#ef4444' },
                          '&:hover': {
                            background: alpha('#ef4444', 0.18),
                            boxShadow: '0 6px 16px rgba(239,68,68,0.45)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': { transform: 'scale(0.96)' },
                        }}
                      >
                        <Icon>delete_outline</Icon>
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </>
              )}
            </MDBox>
          ) : null}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
